/**
 * DOCUMENTATION:
 * Component: CleanCodeErrors
 * Purpose: Teaching robust error handling and defensive programming techniques.
 * * * Note for Junior Developers:
 * - Rule 1: Fail Fast. Detect problems at the beginning of a function and exit immediately.
 * - Rule 2: Use Guard Clauses. They keep your code "flat" and readable.
 * - Rule 3: Don't return 'null'. It's a landmine for the next developer. Return empty arrays
 * or throw meaningful errors instead.
 * - Rule 4: Logic first, Error Handling second. Try to separate your "happy path"
 * from your "disaster recovery" logic.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
    },
}));

/* ✅ Main Component */
export default function CleanCodeErrors() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const errorRules = useMemo(() => [
        { rule: "Guard Clauses", description: "The 'Bouncer' pattern. Check for invalid inputs at the very top of the function and return immediately if something is wrong." },
        { rule: "Exception Context", description: "When throwing an error, provide context. 'Error: Failed' is useless. 'Error: Failed to fetch user with ID 502' is helpful." },
        { rule: "Don't Hide Errors", description: "Avoid empty catch blocks. Swallowing an error makes it impossible to debug when things inevitably break in production." },
        { rule: "Return Empty Collections", description: "If a function returns a list, return an empty array [] instead of null. This prevents 'Cannot read property map of null' errors." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. Flattening the Pyramid (Guard Clauses)",
            code: `// ❌ JUNIOR: Nested 'if' statements (The Pyramid of Doom)\nfunction registerUser(user) {\n  if (user !== null) {\n    if (user.isValid()) {\n      saveToDatabase(user);\n      sendEmail(user);\n    }\n  }\n}\n\n// ✅ SENIOR: Guard Clauses keep the code flat and readable\nfunction registerUser(user) {\n  if (!user || !user.isValid()) return;\n\n  saveToDatabase(user);\n  sendEmail(user);\n}`,
            desc: "Guard clauses act as filters. If the requirements aren't met, you exit early. This leaves the 'Happy Path' of your code un-indented and easy to follow."
        },
        {
            title: "2. The Danger of Returning Null",
            code: `// ❌ BAD: Forces every caller to check for null\nfunction getActiveUsers() {\n  const users = db.fetch();\n  return users.length > 0 ? users : null;\n}\n\n// ✅ GOOD: Callers can safely use .map() or .length without crashing\nfunction getActiveUsers() {\n  const users = db.fetch();\n  return users || []; \n}`,
            desc: "Always try to return a value that matches the expected type. If the caller expects an array, give them an empty array, not a null object."
        },
        {
            title: "3. Meaningful Try-Catch Blocks",
            code: `// ❌ BAD: Silent failure. No one knows this failed.\ntry {\n  uploadFile(file);\n} catch (e) {}\n\n// ✅ GOOD: Log the error and inform the user\ntry {\n  uploadFile(file);\n} catch (error) {\n  logger.error(\`File upload failed for \${file.name}\`, error);\n  showToast('Upload failed. Please try again.');\n}`,
            desc: "Errors are inevitable. A clean coder doesn't try to hide them; they handle them gracefully and provide a clear path forward for both the developer and the user."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    ERROR HANDLING & DEFENSE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Expect the Unexpected
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            In a laboratory, code works perfectly. In the real world, batteries die, users type emojis into number fields, and the internet goes down. <strong>Defensive coding</strong> is the practice of protecting your code against the environment it lives in.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Defensive Strategies</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {errorRules.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.rule}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem" }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#e0b200" },
}));
